import React, { useEffect } from 'react'

import { PlusCircleIcon } from '@heroicons/react/24/outline'

import { AddNewButton } from '../components/AddNewButton'
import Review from './Escalation'
import { setAddEscalationDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import useDocuments from '@/features/FileManager/useDocuments'
import { useCurrentApplication } from '@/graphql/services/application'
import {
  useCreateEscalationMutation,
  useLazyGetEscalationsQuery,
  useMarkAsSeenEscalationsMutation,
} from '@/services/escalations'
import {
  useComputeAutomatedApplicationFlagsQuery,
  useLazyComputeAutomatedApplicationExceptionsByApplicationQuery,
} from '@/services/v2-application/applicationException'
import { Escalation } from '@/types/unlock'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'

export const Escalations = ({ escalationsCountRefetch }) => {
  const dispatch = useAppDispatch()
  const activeTab = useAppSelector((state) => state.general.dealAdvisorSubTabs.escalations)

  const { data: documents } = useDocuments()
  const { data: application } = useCurrentApplication()

  const adddEscalationEnabled = useFeatureFlag(FEATURES_LIST.ADD_ESCALATION_DRAWER)

  const [getEscalationsQuery, { data: escalations }] = useLazyGetEscalationsQuery()

  const [computeApplicationAutomatedExceptions, { data: automatedExceptions }] =
    useLazyComputeAutomatedApplicationExceptionsByApplicationQuery()

  const { data: automatedFlags } = useComputeAutomatedApplicationFlagsQuery(
    {
      applicationUUID: application?.uuid,
    },
    { skip: !application?.uuid }
  )

  const [markAsSeenEscalations] = useMarkAsSeenEscalationsMutation()
  const [createEscalation] = useCreateEscalationMutation()

  useEffect(() => {
    async function fetchEscalations() {
      await computeApplicationAutomatedExceptions({ applicationUUID: application?.uuid })
      await getEscalationsQuery(application?.externalId)
      escalationsCountRefetch()
    }
    if (application) {
      fetchEscalations().catch(console.error)
    }
  }, [automatedFlags, automatedExceptions])

  useEffect(() => {
    async function markAsSeen() {
      await markAsSeenEscalations({ application: application.id, tab: activeTab })

      escalationsCountRefetch()
    }

    if (application) {
      markAsSeen()
    }
  }, [markAsSeenEscalations, escalationsCountRefetch, application, activeTab])

  const handleCreateEscalation = async () => {
    const getEscalationTexts = (tab) => {
      switch (tab) {
        case 'manager':
          return {
            type: 'manager-review',
            title: 'Manager Review',
          }

        default:
          return {}
      }
    }

    const partialEscalation: Partial<Escalation> = {
      ...getEscalationTexts(activeTab),
      application: application?.id,
      approved: false,
      tab: activeTab,
      seen: true,
    }

    createEscalation(partialEscalation)
  }

  if (!application) {
    return <p className='p-4'>Please select an application</p>
  }

  return (
    <div className='p-2'>
      <div className='flex justify-end items-center'>
        {adddEscalationEnabled && (
          <AddNewButton onClick={() => dispatch(setAddEscalationDrawerOpen(true))}>
            Add New
          </AddNewButton>
        )}
      </div>

      {activeTab !== 'exceptions' && (
        <div
          className='flex justify-between rounded-md
            bg-sky-200 text-sm font-bold
            py-2 px-2 mt-4'
        >
          <p>Add Escalation</p>
          <PlusCircleIcon
            width={20}
            className='cursor-pointer'
            onClick={() => handleCreateEscalation()}
          />
        </div>
      )}

      <div>
        {escalations &&
          escalations.map((escalation) => {
            if (escalation.tab != activeTab) return null

            return (
              <Review
                key={escalation.uuid}
                escalation={escalation}
                documents={documents}
              />
            )
          })}
      </div>
    </div>
  )
}
